import ReactECharts from "echarts-for-react";
import { useEffect, useMemo, useRef } from "react";
import type { TsPoint } from "@/data/types";
import { baseOption, chartTokens, hexA, readVar, tooltipHtml } from "@/lib/chartTheme";
import { prepareLineSeriesWithSpans, type BridgedSpan } from "@/lib/chartGaps";
import { useThemeVersion } from "@/hooks/useThemeVersion";
import { LOCALE, formatDateTime } from "@/lib/format";

/** Tooltip value formatter — locale-grouped, ≤ 2 decimals (DSGN-09). */
const TOOLTIP_NUM = new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 2 });

export interface HistorySeries {
  name: string;
  unit: string;
  color: string;
  yAxis?: number;
  data: TsPoint[];
  type?: "line" | "bar" | "step";
  fill?: boolean;
  /** Optional ghost series (e.g. yesterday) — drawn faded under main. */
  ghost?: TsPoint[];
}

/**
 * Multi-series chart for the Historical analyzer and per-BESS detail.
 *
 * Features:
 *   - Multi-axis: any series can declare `yAxis: 0..N` so different units
 *     (kW / % / EUR/MWh) get their own scale.
 *   - DataZoom: drag the slider at the bottom OR pinch/scroll-wheel inside
 *     the chart to zoom in. The slider always reflects the visible range.
 *   - Crosshair tooltip: shows every series's value at the hovered ts.
 *   - Gradient fills under filled series for visual weight.
 *   - Theme-aware: text/grid/tooltip read from CSS custom properties so the
 *     chart auto-flips with the dark-mode toggle.
 *   - Zoom → range sync (HIST-05): when `onWindowChange` is given, each
 *     datazoom gesture reports the zoomed absolute window (~500 ms
 *     debounced) so the host page can adopt it as the shared time range.
 *     Without the prop, zoom behaves exactly as before (display-only).
 *   - Honest bridges (HIST-14): the gap policy's bridged spans (computed
 *     here, where the data passes through chartGaps) are painted as
 *     subtle stale-toned dashed markAreas and their tooltip rows are
 *     suffixed "(interpolated)" so invented samples are never mistaken
 *     for measurements.
 */
export function HistoryChart({ series, height = 320, onWindowChange }: {
  series: HistorySeries[];
  height?: number;
  /** Fired (debounced) with the zoomed [startTs, endTs] after a datazoom gesture. */
  onWindowChange?: (startTs: number, endTs: number) => void;
}) {
  const chartRef = useRef<ReactECharts | null>(null);
  // Bumps when [data-theme] flips so chartTokens() are re-read and the
  // option repaints in the new theme's colours (DSGN-07).
  const themeVersion = useThemeVersion();

  // Full x-extent of the plotted data — needed to translate percentage-
  // based datazoom events into absolute timestamps.
  const xExtent = useMemo<[number, number] | null>(() => {
    let min = Infinity;
    let max = -Infinity;
    for (const s of series) {
      for (const p of s.data) {
        if (p.ts < min) min = p.ts;
        if (p.ts > max) max = p.ts;
      }
    }
    return max > min ? [min, max] : null;
  }, [series]);

  // Refs keep the datazoom handler stable across renders (echarts-for-react
  // binds onEvents once) while always reading the latest callback/extent.
  const onWindowChangeRef = useRef(onWindowChange);
  onWindowChangeRef.current = onWindowChange;
  const xExtentRef = useRef(xExtent);
  xExtentRef.current = xExtent;
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  const hasWindowChange = onWindowChange != null;
  const onEvents = useMemo(() => {
    if (!hasWindowChange) return undefined;
    return {
      datazoom: () => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
          const cb = onWindowChangeRef.current;
          const inst = chartRef.current?.getEchartsInstance();
          if (!cb || !inst) return;
          // After a zoom ECharts normalises the dataZoom state into the
          // option: startValue/endValue hold absolute axis values when
          // available, start/end the percentages of the full extent.
          const opt = inst.getOption() as {
            dataZoom?: Array<{ startValue?: number; endValue?: number; start?: number; end?: number }>;
          };
          const dz = opt.dataZoom?.[0];
          if (!dz) return;
          let startTs = typeof dz.startValue === "number" ? dz.startValue : null;
          let endTs = typeof dz.endValue === "number" ? dz.endValue : null;
          const extent = xExtentRef.current;
          if ((startTs == null || endTs == null) && extent) {
            const span = extent[1] - extent[0];
            if (startTs == null && typeof dz.start === "number") startTs = extent[0] + (dz.start / 100) * span;
            if (endTs == null && typeof dz.end === "number") endTs = extent[0] + (dz.end / 100) * span;
          }
          if (startTs == null || endTs == null || endTs <= startTs) return;
          cb(Math.round(startTs), Math.round(endTs));
        }, 500);
      },
    };
  }, [hasWindowChange]);

  const option = useMemo(() => {
    const base = baseOption();
    const t = chartTokens();
    // Stale/frozen-data token — bridged (interpolated) spans are stale
    // vocabulary, never severity or direction paint (HIST-14).
    const stale = readVar("--ems-stale");
    const yAxes = collectYAxes(series);
    const eSeries: object[] = [];

    // Series-name → unit lookup for the tooltip (ghost rows inherit the
    // parent series' unit).
    const unitByName = new Map<string, string>();
    for (const s of series) {
      unitByName.set(s.name, s.unit);
      if (s.ghost?.length) unitByName.set(`${s.name} · prior`, s.unit);
    }
    // Series-name → bridged spans (HIST-14): everything strictly inside
    // a span is synthetic — the tooltip flags those rows below.
    const spansByName = new Map<string, BridgedSpan[]>();

    for (const s of series) {
      // Ghost (comparison) series — drawn underneath as a faded dashed
      // line, no fill. This way the main series's gradient sits on top.
      if (s.ghost && s.ghost.length) {
        const ghostPrep = prepareLineSeriesWithSpans(s.ghost, { step: s.type === "step" });
        if (ghostPrep.bridgedSpans.length) spansByName.set(`${s.name} · prior`, ghostPrep.bridgedSpans);
        eSeries.push({
          name: `${s.name} · prior`,
          type: s.type === "step" ? "line" : s.type ?? "line",
          step: s.type === "step" ? "end" : undefined,
          yAxisIndex: s.yAxis ?? 0,
          showSymbol: false,
          smooth: 0.25,
          sampling: "lttb",
          // Same honest-gap treatment as the main series: a hole in the
          // prior-period comparison must read as a break, not a bridge.
          connectNulls: false,
          lineStyle: { width: 1.4, color: s.color, type: "dashed", opacity: 0.5 },
          itemStyle: { color: s.color, opacity: 0.5 },
          data: ghostPrep.points,
          z: 1,
        });
      }
      const prep = prepareLineSeriesWithSpans(s.data, { step: s.type === "step" });
      if (prep.bridgedSpans.length) spansByName.set(s.name, prep.bridgedSpans);
      eSeries.push({
        name: s.name,
        type: s.type === "step" ? "line" : s.type ?? "line",
        step: s.type === "step" ? "end" : undefined,
        yAxisIndex: s.yAxis ?? 0,
        showSymbol: false,
        smooth: 0.3,
        sampling: "lttb",
        emphasis: { focus: "series" },
        // Gap policy lives in `lib/chartGaps.ts` — short/medium holes
        // get filled with synthetic interpolated samples so the line
        // stays smooth, while real outages produce an explicit null
        // that ECharts honours via connectNulls:false.
        connectNulls: false,
        lineStyle: { width: 2.2, color: s.color },
        itemStyle: { color: s.color },
        areaStyle: s.fill
          ? {
              opacity: 1,
              color: {
                type: "linear", x: 0, y: 0, x2: 0, y2: 1,
                colorStops: [
                  { offset: 0,    color: hexA(s.color, 0.28) },
                  { offset: 0.45, color: hexA(s.color, 0.12) },
                  { offset: 1,    color: hexA(s.color, 0)    },
                ],
              },
            }
          : undefined,
        data: prep.points,
        // Honest bridges (HIST-14): every gap-policy bridge gets a subtle
        // stale-toned, dash-bordered band so the operator can see at a
        // glance which stretches are interpolated, not measured. Only x
        // coordinates — the band covers the series' full y extent.
        markArea: prep.bridgedSpans.length
          ? {
              silent: true,
              itemStyle: {
                color: hexA(stale, 0.08),
                borderColor: hexA(stale, 0.45),
                borderWidth: 1,
                borderType: "dashed",
              },
              data: prep.bridgedSpans.map((sp) => [{ xAxis: sp.startTs }, { xAxis: sp.endTs }]),
            }
          : undefined,
        z: 2,
      });
    }

    return {
      ...base,
      // grid.top reserves a full row for the legend so legend text never
      // collides with the plot / top tick labels (WALK-06). No rotated
      // axis names — the unit rides on every tick label instead.
      grid: { ...base.grid, top: 40, bottom: 44, right: 16 + Math.max(0, yAxes.length - 2) * 50 },
      tooltip: {
        ...(base.tooltip as object),
        // DSGN-09: every hovered value carries its series' unit — rendered
        // through the shared tooltipHtml() card like the other charts.
        formatter: (params: Array<{ axisValue: number | string; seriesName: string; color: string; value: [number, number | null] }>) => {
          const list = Array.isArray(params) ? params : [params];
          if (!list.length) return "";
          const ts = Number(list[0].axisValue);
          // A row is interpolated when its own point ts falls STRICTLY
          // inside one of its series' bridged spans — the span endpoints
          // are real samples (HIST-14).
          const interpolated = (name: string, rowTs: number) => {
            const spans = spansByName.get(name);
            if (!spans || !Number.isFinite(rowTs)) return false;
            return spans.some((sp) => rowTs > sp.startTs && rowTs < sp.endTs);
          };
          return tooltipHtml({
            header: Number.isFinite(ts) ? formatDateTime(ts) : String(list[0].axisValue ?? ""),
            rows: list.map((p) => {
              const rowTs = Array.isArray(p.value) ? Number(p.value[0]) : NaN;
              const raw = Array.isArray(p.value) ? p.value[1] : null;
              const ok = typeof raw === "number" && Number.isFinite(raw);
              return {
                color: String(p.color),
                label: ok && interpolated(p.seriesName, rowTs)
                  ? `${p.seriesName} (interpolated)`
                  : p.seriesName,
                value: ok ? TOOLTIP_NUM.format(raw) : "—",
                unit: ok ? unitByName.get(p.seriesName) || undefined : undefined,
              };
            }),
          });
        },
      },
      yAxis: yAxes.map((u, idx) => ({
        type: "value",
        position: idx === 0 ? "left" : "right",
        offset: idx > 1 ? (idx - 1) * 50 : 0,
        scale: true,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: t.textMuted,
          fontSize: 11,
          formatter: (v: number) => formatNice(v, u),
        },
        splitLine: { lineStyle: { color: t.grid, type: "dashed" } },
      })),
      dataZoom: [
        // Inside: scroll wheel + pinch + drag horizontally to zoom/pan.
        { type: "inside", xAxisIndex: 0, filterMode: "none", zoomOnMouseWheel: true, moveOnMouseWheel: false, throttle: 50 },
        // Slider: thin handle below the chart with a brush preview.
        {
          type: "slider",
          xAxisIndex: 0,
          filterMode: "none",
          bottom: 4,
          height: 20,
          handleSize: 18,
          showDetail: false,
          borderColor: t.tooltipBorder,
          backgroundColor: hexA(t.textFaint, 0.06),
          fillerColor: hexA(t.accent, 0.12),
          handleStyle: { color: t.accent, borderColor: t.accent },
          moveHandleStyle: { color: t.accent },
          dataBackground: {
            lineStyle: { color: hexA(t.textFaint, 0.4), width: 1 },
            areaStyle: { color: hexA(t.textFaint, 0.08) },
          },
          selectedDataBackground: {
            lineStyle: { color: t.accent, width: 1 },
            areaStyle: { color: hexA(t.accent, 0.18) },
          },
          labelFormatter: (_n: number, s: string) => s,
        },
      ],
      series: eSeries,
      legend: {
        ...base.legend,
        top: 6,
        right: 12,
        data: series.flatMap((s) => s.ghost?.length ? [s.name, `${s.name} · prior`] : [s.name]),
      },
    } as object;
  }, [series, themeVersion]);

  return <ReactECharts ref={chartRef} option={option} notMerge style={{ height }} lazyUpdate onEvents={onEvents} />;
}

function collectYAxes(series: HistorySeries[]) {
  const out: string[] = [];
  for (const s of series) {
    const idx = s.yAxis ?? 0;
    if (out[idx] == null) out[idx] = s.unit;
  }
  return out;
}

function formatNice(v: number, unit?: string) {
  if (unit === "%") return `${v.toFixed(0)}%`;
  if (unit === "EUR/MWh") return `€${v.toFixed(0)}`;
  if (unit === "V") return `${v.toFixed(0)} V`;
  // Default: always carry the axis unit on the tick label (DSGN-09) —
  // the kW axis used to fall through to a naked number here.
  const u = unit ? ` ${unit}` : "";
  if (Math.abs(v) >= 1e6) return `${(v / 1e6).toFixed(1)}M${u}`;
  if (Math.abs(v) >= 999) return `${(v / 1000).toFixed(1)}k${u}`;
  return `${v.toFixed(0)}${u}`;
}
